import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/VideoModal.css";

interface VideoModalProps {
  isOpen: boolean;
  onClose: (currentTime?: number, wasPlaying?: boolean) => void;
  videoSrc: string;
  title: string;
  initialTime?: number;
  autoPlay?: boolean;
}

const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  videoSrc,
  title,
  initialTime = 0,
  autoPlay = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleLoadedMetadata = async () => {
      setDuration(video.duration);
      setCurrentTime(initialTime);
      video.currentTime = initialTime;

      // Auto-play if requested
      if (autoPlay && isOpen) {
        try {
          await video.play();
        } catch (error) {
          console.error("Error auto-playing video:", error);
        }
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    // Reset video when modal opens
    if (isOpen && video.src) {
      video.load();
      setIsPlaying(false);
      setCurrentTime(initialTime);
      setDuration(0);
    }

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [isDragging, isOpen, videoSrc, initialTime, autoPlay]);

  const togglePlayPause = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
      } else {
        await video.play();
      }
    } catch (error) {
      console.error("Error playing video:", error);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) return; // Don't handle click when dragging

    const progressBar = progressRef.current;
    const video = videoRef.current;
    if (!progressBar || !video || duration === 0) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = Math.max(
      0,
      Math.min((clickX / rect.width) * duration, duration)
    );

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleProgressUpdate(e);
  };

  const handleProgressUpdate = (e: React.MouseEvent<HTMLDivElement>) => {
    const progressBar = progressRef.current;
    const video = videoRef.current;
    if (!progressBar || !video || duration === 0) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = Math.max(
      0,
      Math.min((clickX / rect.width) * duration, duration)
    );

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      handleProgressUpdate(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Global mouse up handler for when user releases outside the progress bar
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleClose = () => {
    const video = videoRef.current;
    if (video) {
      onClose(video.currentTime, isPlaying);
    } else {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="video-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="video-modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="video-modal-close" onClick={handleClose}>
              ✕
            </button>

            <div className="video-modal-player">
              <video
                ref={videoRef}
                className="video-modal-video"
                src={videoSrc}
                onClick={togglePlayPause}
              />

              <div className="video-controls">
                <button className="play-pause-btn" onClick={togglePlayPause}>
                  {isPlaying ? "⏸" : "▶"}
                </button>

                <div className="video-time">{formatTime(currentTime)}</div>

                <div
                  className="progress-container"
                  ref={progressRef}
                  onClick={handleProgressClick}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setIsDragging(false)}
                >
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progressPercentage}%` }}
                    />
                    <div
                      className="progress-thumb"
                      style={{ left: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                <div className="video-time">{formatTime(duration)}</div>
              </div>
            </div>

            <div className="video-modal-title">{title}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;
